/**
 * The arithmetic behind the Event calendar: which days a view covers, and where
 * a step forward or back lands.
 *
 * Its own module because "a week runs Monday to Sunday" is a decision about the
 * product, not about a grid — and because the grids then take a list of days
 * rather than a date and a mode, so none of them has to know where a week
 * begins.
 *
 * Everything works in the reader's own timezone, deliberately: a calendar that
 * renders Tuesday's 9am as Monday because the machine is west of the office is
 * worse than one that never mentions a timezone at all. The date arithmetic goes
 * through the Date constructor's component form rather than adding milliseconds,
 * so it stays correct across a DST boundary — Malaysia has none, but nothing
 * says the reader is in Malaysia.
 */

export type CalendarView = 'day' | 'week' | 'month';

/**
 * Weeks start on Monday, so the weekend is one block at the end rather than a
 * day sawn off each edge. Sunday-first is the US default that every Date method
 * hands back, which is why this offset exists in the first place.
 */
const WEEK_STARTS_ON = 1;

export const DAYS_IN_WEEK = 7;

/**
 * Six week rows, always, even for a February that fits in four.
 *
 * A grid that grew and shrank month to month would change its row height as you
 * paged through it, so an event block would be a different size in March than in
 * April and the header row would jump under the pointer. Fixed rows cost a
 * trailing week of the next month in the short cases; that week is greyed, and a
 * stable grid is worth it.
 */
export const WEEKS_IN_MONTH_GRID = 6;

/** Midnight at the start of the day `date` falls in. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * The same day-of-month in another month, pulled back to the last day where that
 * month is too short: a step forward from 31 March lands on 30 April, not on 1
 * May. Without the clamp, paging forward can skip a month entirely.
 */
export function addMonths(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
}

/** The Monday of the week `date` falls in. */
export function startOfWeek(date: Date): Date {
  const offset = (date.getDay() - WEEK_STARTS_ON + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  return addDays(startOfDay(date), -offset);
}

/** The first of the month `date` falls in. */
export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/**
 * Every day a view puts on screen, in order: one for a day, seven for a week,
 * and six Monday-led rows for a month — which is why a month starts at the
 * Monday on or before the 1st and can open with days of the month before it.
 */
export function daysOf(view: CalendarView, anchor: Date): Date[] {
  if (view === 'day') return [startOfDay(anchor)];

  const first = view === 'week' ? startOfWeek(anchor) : startOfWeek(startOfMonth(anchor));
  const length = view === 'week' ? DAYS_IN_WEEK : DAYS_IN_WEEK * WEEKS_IN_MONTH_GRID;
  return Array.from({ length }, (_, index) => addDays(first, index));
}

/**
 * Where the previous / next control lands: one unit of whatever is on screen, so
 * paging a week never leaves the reader mid-week and paging a month never lands
 * on the 3rd of March because February was short (see `addMonths`).
 */
export function stepAnchor(view: CalendarView, anchor: Date, direction: 1 | -1): Date {
  switch (view) {
    case 'day':
      return addDays(anchor, direction);
    case 'week':
      return addDays(anchor, direction * DAYS_IN_WEEK);
    case 'month':
      return addMonths(anchor, direction);
    default: {
      const unreachable: never = view;
      return unreachable;
    }
  }
}

/**
 * How far through the day a moment sits, 0 to 1 — the time grid's only bridge
 * from a clock to a position, used to place the current-time line.
 */
export function dayFraction(at: Date): number {
  const minutes = at.getHours() * 60 + at.getMinutes() + at.getSeconds() / 60;
  return minutes / (24 * 60);
}
